import { BaseSymbol } from '@ui/assets/config/trade-pair-listings';
import { Amount } from '@ui/components/Amount/Amount';
import { AmountUsd } from '@ui/components/Amount/AmountUsd';
import { Pnl } from '@ui/components/Amount/Pnl';
import { Edit, EyeClose, EyeOpen } from '@ui/components/icons';
import { TextTooltip } from '@ui/components/TextTooltip';
import { getChainName } from '@ui/constants/chain';
import { DEFAULT_UNDEFINED_NUMBER_DISPLAY } from '@ui/constants/uiConfig';
import { MarketTimeIndicator } from '@ui/features/RWA/components/MarketTimeIndicator';
import { usePairMarketScheduleStore } from '@ui/features/RWA/hooks/usePairMarketScheduleStore/usePairMarketScheduleStore';
import {
  getPayTokenPriceMapKey,
  usePayTokenPriceLatest,
  usePayTokenPriceLatestMap,
} from '@ui/features/Trade/hooks/usePayTokenPriceLatest';
import { useDataChainId } from '@ui/hooks/datachain';
import { usePriceMapLatest } from '@ui/hooks/price/usePriceLatest';
import { usePriceLiveStore } from '@ui/hooks/price/usePriceLive';
import { useTradePairInfos } from '@ui/hooks/trade/useTradePairInfos';
import { useTokenDisplayDecimals } from '@ui/hooks/useTokenDisplayDecimals';
import { useTokenSizeDisplayDecimals } from '@ui/hooks/useTokenSizeDisplayDecimals';
import { ZERO_BIG_NUMBER } from '@ui/lib/bignumber';
import { Button } from '@ui/lib/park/ui/button';
import { Icon } from '@ui/lib/park/ui/icon';
import { IconButton } from '@ui/lib/park/ui/icon-button';
import { Text } from '@ui/lib/park/ui/text';
import { calculatePositionTotalPoints } from '@ui/services/contract/hooks/GenesisAirdrop/points';
import {
  PositionBasicPoints,
  useGenesisAirdropBatchPositionBasicPoints,
} from '@ui/services/contract/hooks/GenesisAirdrop/useBatchPositionBasicPoints';
import {
  getActiveDecreaseOrdersByPositionHash,
  getActiveSlOrders,
  getActiveTpOrders,
  getDecreaseOrderTriggerPrice,
} from '@ui/services/contract/hooks/TradingPortalFacet/useDecreaseOrders/selectors';
import { useDecreaseOrders } from '@ui/services/contract/hooks/TradingPortalFacet/useDecreaseOrders/useDecreaseOrders';
import type { DecreaseOrderRow } from '@ui/services/contract/hooks/TradingPortalFacet/useDecreaseOrders/types';
import type { Position } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV3/types';
import { getProperLeverageMargin } from '@ui/utils/trade/getProperLeverageMargin';
import { useTranslation } from 'next-i18next';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Grid, HStack, Stack, styled, VStack } from 'styled-system/jsx';
import { shallow } from 'zustand/shallow';

import { useIsTradePageContainerDesktop } from '../../hooks/useIsTradePageContainerDesktop';
import { useSetTradingPair } from '../../store/useSetTradingPair';
import { useTradingPair } from '../../store/useTradingPair';
import { MarginOverlay } from '../MarginOverlay/MarginOverlay';
import { CurrentTradingPairToggle } from '../OrderList/CurrentTradingPairToggle';
import { formatLeverage } from '../TradeModule/utils/formatLeverage';
import { useChartLineStore } from '../PriceChart/useChartLineStore';
import { SizeUsd } from '../SizeUsd';
import { CardMobile } from './CardMobile';
import { CollateralTooltipContent } from './CollateralTooltipContent';
import { PositionListContextProvider } from './context';
import { InitialCollateral } from './InitialCollateral';
import { LiquidationPrice } from './LiquidationPrice';
import {
  PairSideCell,
  PositionSidePill,
} from '../shared/PairSideCell';
import { MarketPrice } from './MarketPrice';
import { NetValue } from './NetValue';
import { PointsTooltipContent } from './PointsTooltipContent';
import { PositionCloseButton } from './PositionCloseButton';
import { PositionDuration } from './PositionDuration';
import { PositionPnl } from './PositionPnl';
import { PositionShareOverlay } from './PositionShareOverlay';
import { PositionTpSlEditButton } from './PositionTpSlEditButton';
import { RemainingCollateral } from './RemainingCollateral';
import { subValueStyle, valueStyle } from './styles';
import { PositionMarketData } from './types';
import { useBatchCloseTrade } from './useBatchCloseTrade';

const getFilterIsCurrentPair = (baseSymbol: BaseSymbol) => (item: Position) => {
  return item.pair.baseSymbol === baseSymbol;
};
export const PositionList = ({
  positions,
  emptyView,
  showCurrentPairToggle = true,
  showHeaderTotals = false,
}: {
  positions: Position[];
  emptyView: ReactNode;
  showCurrentPairToggle?: boolean;
  showHeaderTotals?: boolean;
}) => {
  const { value: isDesktop, isPending: isBreakpointPending } =
    useIsTradePageContainerDesktop();
  const [isCurrentPairOnly, setIsCurrentPairOnly] = useState(false);

  const tradingPair = useTradingPair((v) => v.useTradingPair());
  const { position: _chartLinePosition, set } = useChartLineStore();
  const { data: decreaseOrders } = useDecreaseOrders();
  const activeDecreaseOrdersByPositionHash = useMemo(
    () => getActiveDecreaseOrdersByPositionHash(decreaseOrders),
    [decreaseOrders],
  );

  // pick the latest position (by timestamp) for a given baseSymbol
  const getLatestPositionForPair = useCallback(
    (list: Position[], baseSymbol: BaseSymbol): Position | undefined => {
      return list
        .filter((p) => p.pair.baseSymbol === baseSymbol)
        .reduce<Position | undefined>((latest, p) => {
          if (!latest) return p;

          return p.timestamp > latest.timestamp ? p : latest;
        }, undefined);
    },
    [],
  );

  // 1) When switching pair or pos length changed (open, close), set latest position of that pair
  useEffect(() => {
    if (!tradingPair) return;
    const latest = getLatestPositionForPair(positions, tradingPair.baseSymbol);
    set({ position: latest ?? null });
    // only respond to pair switching; new positions are handled below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positions.length, tradingPair?.baseSymbol, set]);

  // 2)
  useEffect(() => {
    if (!_chartLinePosition) return;
    const samePosition = positions.find((p) => {
      return p.id === _chartLinePosition?.id;
    });

    if (samePosition) {
      set({ position: samePosition });
    }
  }, [positions, _chartLinePosition, set]);

  const filteredPositions = useMemo(() => {
    const filters: ((item: Position) => boolean)[] = [];
    // mobile only show current pair
    if (isCurrentPairOnly && tradingPair) {
      filters.push(getFilterIsCurrentPair(tradingPair.baseSymbol));
    }

    return filters.reduce((acc, filter) => acc.filter(filter), positions);
  }, [isCurrentPairOnly, positions, tradingPair.baseSymbol]);

  const pairs = useTradePairInfos();
  const tradePairByAddress = useMemo(() => {
    return new Map(
      (pairs ?? []).flatMap((pair) => [
        [pair.address.toLowerCase(), pair],
        [pair.base.toLowerCase(), pair],
      ]),
    );
  }, [pairs]);
  const baseSymbols = useMemo(
    () =>
      Array.from(
        new Set(filteredPositions.map((position) => position.pair.baseSymbol)),
      ),
    [filteredPositions],
  );
  const livePriceBySymbol = usePriceLiveStore(
    useCallback(
      (s) =>
        Object.fromEntries(
          baseSymbols.map((symbol) => [symbol, s.priceRecord?.[symbol]]),
        ),
      [baseSymbols],
    ),
    shallow,
  );
  const collateralPriceMap = usePayTokenPriceLatestMap(
    useMemo(
      () => filteredPositions.map((position) => position.collateralToken),
      [filteredPositions],
    ),
  );
  const { data: latestPriceMap } = usePriceMapLatest();
  const liquidationPriceByPositionId = useMemo(() => {
    return Object.fromEntries(
      filteredPositions.map((position) => {
        const collateralMarketPrice =
          collateralPriceMap[getPayTokenPriceMapKey(position.collateralToken)];
        const tradePair = tradePairByAddress.get(
          position.pair.address.toLowerCase(),
        );
        const latestPairPrice = latestPriceMap?.[position.pair.baseSymbol];
        const leverage = position
          .getLeverage({
            collateralTokenMarketPrice:
              collateralMarketPrice ?? ZERO_BIG_NUMBER,
          })
          .toNumber();
        const liqLostP = tradePair
          ? getProperLeverageMargin(tradePair.leverageMargins, leverage)
              ?.liqLostP
          : undefined;
        const liquidationPrice =
          tradePair?.feeConfig && latestPairPrice && tradePair
            ? position.getLiquidationPrice({
                collateralMarketPrice: collateralMarketPrice ?? ZERO_BIG_NUMBER,
                liqLostP,
                feeConfig: tradePair.feeConfig,
                pairMarketPrice: latestPairPrice,
                tradePair,
              })
            : undefined;

        return [
          position.id,
          liquidationPrice satisfies PositionMarketData['liquidationPrice'],
        ];
      }),
    );
  }, [
    collateralPriceMap,
    filteredPositions,
    latestPriceMap,
    tradePairByAddress,
  ]);
  const positionMarketDataById = useMemo(() => {
    return Object.fromEntries(
      filteredPositions.map((position) => {
        const pairMarketPrice = livePriceBySymbol[position.pair.baseSymbol];
        const collateralMarketPrice =
          collateralPriceMap[getPayTokenPriceMapKey(position.collateralToken)];
        const tradePair = tradePairByAddress.get(
          position.pair.address.toLowerCase(),
        );

        return [
          position.id,
          {
            pairMarketPrice,
            collateralMarketPrice,
            tradePair,
            liquidationPrice: liquidationPriceByPositionId[position.id],
          } satisfies PositionMarketData,
        ];
      }),
    );
  }, [
    collateralPriceMap,
    filteredPositions,
    liquidationPriceByPositionId,
    livePriceBySymbol,
    tradePairByAddress,
  ]);

  const { data: pointsMap } =
    useGenesisAirdropBatchPositionBasicPoints(filteredPositions);

  const chainId = useDataChainId();
  const marketSchedules = usePairMarketScheduleStore(
    (s) => s._marketSchedule[getChainName(chainId)],
  );

  const closablePositions = useMemo(() => {
    return filteredPositions.filter((p) => {
      const schedule = marketSchedules?.[p.pair.pairName];

      return schedule?.isClientOpen;
    });
  }, [filteredPositions, marketSchedules]);

  const { writeContract: batchClose, isPending: isBatchClosePending } =
    useBatchCloseTrade();

  const handleBatchClose = () => {
    if (closablePositions.length === 0) return;
    batchClose(closablePositions.map((p) => p.id));
  };

  return (
    <>
      {showHeaderTotals && (
        <HStack
          px={4}
          py={2}
          justifyContent="flex-start"
          borderBottom="1px solid"
          borderBottomColor="v2.border.hairline"
          bg="v2.bg.app"
          position="sticky"
          top={0}
          zIndex={11}
          gap={6}
          display={{ base: 'none', desktop: 'flex' }}
        >
          <HeaderTotals positions={filteredPositions} />
        </HStack>
      )}
      {showCurrentPairToggle && (
        <HStack
          css={{
            justifyContent: 'end',
            height: '0',
            // float into the 40px V2 tab bar, vertically centered
            transform: 'translateY(-20px) translateX(-10px)',
          }}
          display={{ base: 'none', desktop: 'flex' }}
          gap={4}
        >
          {closablePositions.length > 1 && (
            <Button
              variant="secondary"
              h="26px"
              px="10px"
              py="0"
              textStyle="v2.label"
              onClick={handleBatchClose}
              loading={isBatchClosePending}
            >
              Close All
            </Button>
          )}
          <CurrentTradingPairToggle
            checked={isCurrentPairOnly}
            onCheckedChange={(v) => {
              setIsCurrentPairOnly(v.checked);
            }}
          />
        </HStack>
      )}
      <Grid
        gridTemplateColumns="minmax(170px, 12fr) minmax(90px, 10fr) minmax(65px, 8fr) minmax(90px, 10fr) minmax(80px, 9fr) minmax(90px, 10fr) minmax(70px, 8fr) minmax(105px, 11fr) minmax(40px, 4fr) minmax(60px, 7fr)"
        columnGap={3}
        rowGap={0}
        pl={4}
        pr={1}
        h="32px"
        maxW="full"
        alignItems="center"
        display={{ base: 'none', desktop: 'grid' }}
        borderBottom="1px solid"
        bg="v2.bg.app"
        borderBottomColor="v2.border.hairline"
        position="sticky"
        top={0}
        zIndex={10}
      >
        {/* column order mirrors the V2 mockup positions table */}
        <Stack gap={1}>
          <Text textStyle="v2.tableHeader" color="v2.text.subtle">
            Pair / Side
          </Text>
        </Stack>
        <Stack gap={1} alignItems="flex-start">
          <Text textStyle="v2.tableHeader" color="v2.text.subtle">
            Size
          </Text>
        </Stack>
        <Stack gap={1} alignItems="flex-start">
          <Text textStyle="v2.tableHeader" color="v2.text.subtle">
            Net Value
          </Text>
        </Stack>
        <Stack gap={1} alignItems="flex-start">
          <Text textStyle="v2.tableHeader" color="v2.text.subtle">
            Collateral
          </Text>
        </Stack>
        <Stack gap={1} alignItems="flex-start">
          <Text textStyle="v2.tableHeader" color="v2.text.subtle">
            Entry / Mark
          </Text>
        </Stack>
        <Stack gap={1} alignItems="flex-start">
          <Text textStyle="v2.tableHeader" color="v2.text.subtle">
            PnL (ROE)
          </Text>
        </Stack>
        <Stack gap={1} alignItems="flex-start">
          <Text textStyle="v2.tableHeader" color="v2.text.subtle">
            Liq. Price
          </Text>
        </Stack>
        <Stack gap={1} alignItems="flex-start">
          <Text textStyle="v2.tableHeader" color="v2.text.subtle">
            TP / SL
          </Text>
        </Stack>
        <Stack gap={1} alignItems="flex-start">
          <Text textStyle="v2.tableHeader" color="v2.text.subtle">
            Points
          </Text>
        </Stack>
      </Grid>
      <Stack gap={0} px={{ base: '10px', desktop: 0 }} pt={{ base: 2, desktop: 0 }}>
        {filteredPositions.map((position) => {
          const marketData = positionMarketDataById[position.id];
          const isMarketHourOpen =
            marketSchedules?.[position.pair.pairName]?.isClientOpen ?? true;

          return (
            <PositionListContextProvider
              position={position}
              key={position.id}
              tradePair={marketData?.tradePair}
              isMarketHourOpen={isMarketHourOpen}
            >
              <PositionCard
                position={position}
                activeDecreaseOrders={
                  activeDecreaseOrdersByPositionHash[position.id]
                }
                marketData={marketData}
                pairs={pairs}
                isDesktop={isDesktop}
                isBreakpointPending={isBreakpointPending}
                points={pointsMap?.[position.id]}
                pointsTotalText={(() => {
                  // Show 0 points during countdown window: 3min (normal) / 1min (degen)
                  const nowMs = Date.now();
                  const countdownMs = position.pair.isDegenPair
                    ? 60_000
                    : 180_000;
                  const isInCountdown =
                    nowMs < position.timestamp * 1000 + countdownMs;
                  if (isInCountdown) return '0';

                  const p = pointsMap?.[position.id];
                  if (!p) return undefined;
                  const total = calculatePositionTotalPoints(p);

                  return total > 0 ? total.toLocaleString() : undefined;
                })()}
              />
            </PositionListContextProvider>
          );
        })}
      </Stack>
      {filteredPositions.length === 0 && emptyView}
    </>
  );
};

const HeaderTotals = ({ positions }: { positions: Position[] }) => {
  const { t } = useTranslation();
  const livePriceMap = usePriceLiveStore();
  const collateralPriceMap = usePayTokenPriceLatestMap(
    positions.map((position) => position.collateralToken),
  );
  const pairs = useTradePairInfos();

  const { totalPnlAfterFee, totalNetValue } = useMemo(() => {
    if (!positions || !livePriceMap) {
      return { totalPnlAfterFee: undefined, totalNetValue: undefined };
    }

    if (Object.values(livePriceMap).some((v) => !v)) {
      return { totalPnlAfterFee: undefined, totalNetValue: undefined };
    }

    const sum = positions.reduce(
      (acc, p) => {
        const pairMarketPrice = livePriceMap.priceRecord?.[p.pair.baseSymbol];
        const collateralMarketPrice =
          collateralPriceMap[getPayTokenPriceMapKey(p.collateralToken)];
        const tradePair = pairs?.find(
          (pair) => pair.base.toLowerCase() === p.pair.address.toLowerCase(),
        );

        const feeConfig = tradePair?.feeConfig;

        if (
          !pairMarketPrice ||
          !collateralMarketPrice ||
          !tradePair ||
          !feeConfig
        ) {
          return acc;
        }

        const pnlAfterFee = p.getPnlAfterFee({
          ...feeConfig,
          pairMarketPrice,
          collateralMarketPrice,
          tradePair,
        });

        const netValue = p.getNetValue({
          ...feeConfig,
          pairMarketPrice,
          collateralMarketPrice,
          tradePair,
        });

        return {
          totalPnlAfterFee: acc.totalPnlAfterFee.plus(pnlAfterFee),
          totalNetValue: acc.totalNetValue.plus(netValue),
        };
      },
      { totalPnlAfterFee: ZERO_BIG_NUMBER, totalNetValue: ZERO_BIG_NUMBER },
    );

    return sum;
  }, [collateralPriceMap, livePriceMap, pairs, positions]);

  return (
    <HStack gap={8} alignItems="center">
      <HStack gap={2} alignItems="center">
        <Text textStyle="v2.meta" color="v2.text.muted">
          {t('common.Total Unrealized PnL(after fees)')}
        </Text>
        <Pnl
          value={totalPnlAfterFee}
          textStyle="numbers.small"
          opts={{ isShowSign: true }}
        />
      </HStack>
      <HStack gap={2} alignItems="center">
        <Text textStyle="v2.meta" color="v2.text.muted">
          {t('common.Total Net Value')}
        </Text>
        <AmountUsd
          textStyle="numbers.small"
          color="v2.text.primary"
          value={totalNetValue}
        />
      </HStack>
    </HStack>
  );
};

const PositionCard = ({
  position,
  activeDecreaseOrders,
  marketData,
  pairs,
  isDesktop,
  isBreakpointPending,
  points,
  pointsTotalText,
}: {
  position: Position;
  activeDecreaseOrders?: DecreaseOrderRow[];
  marketData: PositionMarketData;
  pairs: ReturnType<typeof useTradePairInfos>;
  isDesktop: boolean | null;
  isBreakpointPending: boolean;
  points?: PositionBasicPoints;
  pointsTotalText?: string;
}) => {
  const setTradingPair = useSetTradingPair();
  const currentTradingPair = useTradingPair((v) => v.useTradingPair());
  const setChartLinePosition = useChartLineStore((s) => s.set);
  const priceDisplayDecimals = useTokenDisplayDecimals(
    position?.pair.baseSymbol,
    position?.pair.priceDisplayDecimals,
  );
  const isPendingPosition = position.isPendingPosition;

  const handlePositionClick = () => {
    const isSamePair =
      currentTradingPair &&
      position.pair.baseSymbol === currentTradingPair.baseSymbol &&
      position.pair.isDegenPair === currentTradingPair.isDegenPair;

    if (isSamePair) {
      setChartLinePosition({ position });
      return;
    }

    const targetPair = pairs?.find(
      (p) =>
        p.baseSymbol === position.pair.baseSymbol &&
        p.isDegenPair === position.pair.isDegenPair,
    );
    if (targetPair) {
      setTradingPair(targetPair);
    }
  };

  if (isBreakpointPending) {
    return null;
  }

  return (
    <HStack
      display="grid"
      gap={0}
      data-cy={`PositionCard-${position.isLong ? 'Long' : 'Short'}-${
        position.pair.address
      }`}
      data-cy-id={position.id}
      onClick={handlePositionClick}
      cursor="pointer"
      _hover={{ bg: 'rgba(255, 255, 255, 0.035)' }}
    >
      {isDesktop ? (
        <DesktopPositionCard
          position={position}
          activeDecreaseOrders={activeDecreaseOrders}
          marketData={marketData}
          priceDisplayDecimals={priceDisplayDecimals}
          isPendingPosition={isPendingPosition}
          points={points}
          pointsTotalText={pointsTotalText}
        />
      ) : (
        <CardMobile
          position={position}
          activeDecreaseOrders={activeDecreaseOrders}
          priceDisplayDecimals={priceDisplayDecimals}
          isPendingPosition={isPendingPosition}
          marketData={marketData}
          points={points}
        />
      )}
    </HStack>
  );
};

/** #23: order-count indicator — hidden for 1 (the normal case), "×n" chip beyond */
const TpSlCountBadge = ({ count, show }: { count: number; show?: boolean }) => {
  if (!show || count <= 1) return null;

  return (
    <styled.span
      display="inline-flex"
      alignItems="center"
      px="3px"
      borderRadius="2px"
      border="1px solid"
      borderColor="v2.border.strong"
      color="v2.text.muted"
      fontFamily="Inter"
      fontSize="9px"
      fontWeight="500"
      lineHeight="12px"
      data-cy="TpSlCountBadge"
      data-cy-value={count}
    >
      ×{count}
    </styled.span>
  );
};

const DesktopPositionCard = ({
  position,
  activeDecreaseOrders,
  marketData,
  priceDisplayDecimals,
  isPendingPosition,
  points,
  pointsTotalText,
}: {
  position: Position;
  activeDecreaseOrders?: DecreaseOrderRow[];
  marketData: PositionMarketData;
  priceDisplayDecimals: number;
  isPendingPosition: boolean;
  points?: PositionBasicPoints;
  pointsTotalText?: string;
}) => {
  const { t } = useTranslation();
  const { position: chartLinePosition, set } = useChartLineStore();
  const currentTradingPair = useTradingPair((v) => v.useTradingPair());
  const sizeDisplayDecimals = useTokenSizeDisplayDecimals(
    position?.pair.baseSymbol,
  );

  const isSamePair =
    currentTradingPair &&
    position.pair.baseSymbol === currentTradingPair.baseSymbol;
  const tpOrders = getActiveTpOrders(activeDecreaseOrders);
  const slOrders = getActiveSlOrders(activeDecreaseOrders);
  const firstTpPrice = position.isMergedPosition
    ? getDecreaseOrderTriggerPrice(tpOrders[0])
    : position.takeProfit;
  const firstSlPrice = position.isMergedPosition
    ? getDecreaseOrderTriggerPrice(slOrders[0])
    : position.stopLoss;

  return (
    <Grid
      gridTemplateColumns="minmax(170px, 12fr) minmax(90px, 10fr) minmax(65px, 8fr) minmax(90px, 10fr) minmax(80px, 9fr) minmax(90px, 10fr) minmax(70px, 8fr) minmax(105px, 11fr) minmax(40px, 4fr) minmax(60px, 7fr)"
      columnGap={3}
      rowGap={0}
      pl={4}
      pr={1}
      py="5px"
      gap={0}
      alignItems="center"
      borderBottom="1px solid"
      borderBottomColor="v2.border.hairline"
    >
      {/* Pair / Side — shared PairSideCell (#28): 22px icon vertically
          centered (#25), name line + combined side pill line */}
      <PairSideCell
        image={position.pair.image}
        name={position.pair.baseName}
        css={{ '&:hover .pair-eye': { display: 'inline-flex' } }}
        nameExtras={
          <>
            <MarketTimeIndicator pairName={position.pair.pairName} />
            {isSamePair && (
              <IconButton
                className="pair-eye"
                display="none"
                variant="unstyled"
                size="unstyled"
                boxSize="16px"
                aria-label=""
                onClick={(e) => {
                  e.stopPropagation();
                  set({
                    position:
                      chartLinePosition?.id === position.id ? null : position,
                  });
                }}
              >
                <Icon boxSize="14px">
                  {chartLinePosition?.id === position.id ? (
                    <EyeOpen />
                  ) : (
                    <EyeClose />
                  )}
                </Icon>
              </IconButton>
            )}
          </>
        }
        pill={<PositionSidePill position={position} />}
      />

      <Stack gap="1" alignItems="flex-start">
        {/* <Text css={labelStyle}>{t('common.Size')}</Text> */}
        <Amount
          value={position.size}
          css={valueStyle}
          opts={{
            displayDecimals: sizeDisplayDecimals,
            suffix: ` ${position.pair.baseSymbol}`,
          }}
          data-cy="SizeTokenAmount"
          data-cy-value={position.size}
        />
        <SizeUsd
          genericTradeParams={position}
          css={subValueStyle}
          marketPrice={marketData.pairMarketPrice}
        />
      </Stack>
      <Stack gap="1" alignItems="flex-start">
        {/* <Text css={labelStyle}>{t('common.Net Value')}</Text> */}
        <NetValue position={position} marketData={marketData} />
      </Stack>

      <Stack gap="1" alignItems="flex-start">
        {/* <Text css={labelStyle}>{t('common.Collateral')}</Text> */}
        <HStack gap={0.5} justifyContent="flex-start">
          {isPendingPosition ? (
            DEFAULT_UNDEFINED_NUMBER_DISPLAY
          ) : (
            <HStack gap={0.5}>
              <styled.img
                src={position.collateralToken.image}
                width="14px"
                height="14px"
              />
              <TextTooltip
                content={
                  <CollateralTooltipContent
                    position={position}
                    marketData={marketData}
                  />
                }
              >
                <InitialCollateral
                  position={position}
                  marketPrice={marketData.collateralMarketPrice}
                  data-cy="InitialCollateral"
                />
              </TextTooltip>
            </HStack>
          )}

          {!isPendingPosition && (
            <Box onClick={(e) => e.stopPropagation()}>
              <MarginOverlay position={position}>
                <Button
                  variant="ghost"
                  p={0}
                  w="25px"
                  h="25px"
                  data-cy="AddCollateralButton"
                >
                  <Icon boxSize="12px" color="v2.text.muted">
                    <Edit />
                  </Icon>
                </Button>
              </MarginOverlay>
            </Box>
          )}
        </HStack>
      </Stack>

      <Stack gap="1" alignItems="flex-start">
        {/* <Text css={labelStyle}>{t('common.Entry')}</Text> */}
        <VStack gap={1} alignItems="flex-start">
          <AmountUsd
            value={isPendingPosition ? undefined : position.entryPrice}
            css={valueStyle}
            opts={{
              displayDecimals: priceDisplayDecimals,
            }}
            data-cy="EntryPrice"
            data-cy-value={position.entryPrice}
          />
          {/* Entry and Mark are peer prices — same weight, never sub-styled */}
          <MarketPrice
            position={position}
            css={valueStyle}
            marketPrice={marketData.pairMarketPrice}
            priceDisplayDecimals={priceDisplayDecimals}
          />
        </VStack>
      </Stack>

      <Stack gap="1" alignItems="flex-start">
        {/* <Text css={labelStyle}>{t('common.PnL(after fees)')}</Text> */}
        {/* left-aligned like every other column (table rule) */}
        <PositionPnl
          isStart
          position={position}
          css={valueStyle}
          marketData={marketData}
        />
      </Stack>

      <Stack gap="1" alignItems="flex-start">
        {/* <Text css={labelStyle}>{t('common.Liquidation')}</Text> */}
        <LiquidationPrice
          position={position}
          marketData={marketData}
          priceDisplayDecimals={priceDisplayDecimals}
        />
      </Stack>

      {/* TP / SL — stacked TP-over-SL: single-line reads per the mockup only
          with 2dp prices; real prices + merged "(n)" suffixes overflow the
          track and bleed into the PnL column */}
      <Stack gap="1" alignItems="flex-start" minW={0}>
        <HStack gap={1} justifyContent="flex-start">
          <VStack gap="2px" alignItems="flex-start">
            {!firstTpPrice || firstTpPrice.isZero() ? (
              <Text css={{ ...valueStyle, color: 'v2.text.subtle' }}>—</Text>
            ) : (
              <HStack gap="4px">
                <AmountUsd
                  css={{ ...valueStyle, color: 'v2.long.default' }}
                  value={firstTpPrice}
                  opts={{
                    prefix: '',
                    displayDecimals: priceDisplayDecimals,
                  }}
                  data-cy="TpPrice"
                  data-cy-value={firstTpPrice}
                />
                <TpSlCountBadge count={tpOrders.length} show={position.isMergedPosition} />
              </HStack>
            )}
            {!firstSlPrice || firstSlPrice.isZero() ? (
              <Text css={{ ...valueStyle, color: 'v2.text.subtle' }}>—</Text>
            ) : (
              <HStack gap="4px">
                <AmountUsd
                  css={{ ...valueStyle, color: 'v2.short.default' }}
                  value={firstSlPrice}
                  opts={{
                    prefix: '',
                    displayDecimals: priceDisplayDecimals,
                  }}
                  data-cy="SlPrice"
                  data-cy-value={firstSlPrice}
                />
                <TpSlCountBadge count={slOrders.length} show={position.isMergedPosition} />
              </HStack>
            )}
          </VStack>
          <Box onClick={(e) => e.stopPropagation()}>
            <PositionTpSlEditButton
              position={position}
              activeDecreaseOrders={activeDecreaseOrders}
            />
          </Box>
        </HStack>
      </Stack>

      <Stack gap="1" alignItems="flex-start">
        <TextTooltip
          content={<PointsTooltipContent position={position} points={points} />}
        >
          {(() => {
            const nowMs = Date.now();
            const countdownMs = position.pair.isDegenPair ? 60_000 : 180_000;
            const isInCountdown =
              nowMs < position.timestamp * 1000 + countdownMs;
            if (pointsTotalText !== undefined) {
              return (
                <Text
                  css={{
                    ...valueStyle,
                    color: isInCountdown ? undefined : 'v2.long.default',
                  }}
                >
                  {pointsTotalText}
                </Text>
              );
            }

            return <Text css={valueStyle}>—</Text>;
          })()}
        </TextTooltip>
      </Stack>

      <Stack gap="1" flexDir="row-reverse" alignItems="center">
        <Box onClick={(e) => e.stopPropagation()}>
          <PositionCloseButton position={position} />
        </Box>
        <Box onClick={(e) => e.stopPropagation()}>
          <PositionShareOverlay position={position} />
        </Box>
      </Stack>
    </Grid>
  );
};
